import { ApolloLink, FetchResult, from, Observable } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';

// Keep the complete retry budget below QuestionnaireSkeletonLoader's 10,000 ms
// terminal dialog so a failed KB request reaches the existing error path first.
export const KB_REQUEST_TIMEOUT_MS = 4000;
export const KB_RETRY_MAX_ATTEMPTS = 2;
export const KB_RETRY_INITIAL_DELAY_MS = 300;
export const KB_RETRY_MAX_DELAY_MS = 800;

export const worstCaseKbBudgetMs = (): number =>
  KB_REQUEST_TIMEOUT_MS * KB_RETRY_MAX_ATTEMPTS +
  KB_RETRY_MAX_DELAY_MS * (KB_RETRY_MAX_ATTEMPTS - 1);

export const createTimeoutLink = (
  timeoutMs = KB_REQUEST_TIMEOUT_MS,
): ApolloLink =>
  new ApolloLink((operation, forward) => {
    const controller = new AbortController();
    const fetchOptions = operation.getContext().fetchOptions ?? {};

    operation.setContext({
      fetchOptions: {
        ...fetchOptions,
        signal: controller.signal,
      },
    });

    return new Observable<FetchResult>((observer) => {
      let innerSubscription: { unsubscribe: () => void } | undefined;
      const timer = setTimeout(() => {
        controller.abort();
        innerSubscription?.unsubscribe();
        observer.error(new Error('KB request timed out'));
      }, timeoutMs);

      const clearTimer = () => clearTimeout(timer);

      try {
        innerSubscription = forward(operation).subscribe({
          next: (value) => observer.next(value),
          error: (error) => {
            clearTimer();
            observer.error(error);
          },
          complete: () => {
            clearTimer();
            observer.complete();
          },
        });
      } catch (error) {
        clearTimer();
        observer.error(error);
      }

      return () => {
        clearTimer();
        controller.abort();
        innerSubscription?.unsubscribe();
      };
    });
  });

export const shouldRetryKbRequest = (error: unknown): boolean => {
  const statusCode =
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    (error as { statusCode?: unknown }).statusCode !== undefined
      ? (error as { statusCode: unknown }).statusCode
      : undefined;

  return (
    statusCode === undefined ||
    (typeof statusCode === 'number' && statusCode >= 500)
  );
};

export const createKbRetryLink = (): RetryLink =>
  new RetryLink({
    delay: {
      initial: KB_RETRY_INITIAL_DELAY_MS,
      max: KB_RETRY_MAX_DELAY_MS,
      jitter: true,
    },
    attempts: {
      max: KB_RETRY_MAX_ATTEMPTS,
      retryIf: (error) => shouldRetryKbRequest(error),
    },
  });

export const createKbLink = (httpLink: ApolloLink): ApolloLink =>
  from([createKbRetryLink(), createTimeoutLink(), httpLink]);
